import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { services } from '@/mocks/services';
import { technicians } from '@/mocks/technicians';

interface Meta { title: string; description: string }

const BRAND = 'MENDWELL';

const STATIC_META: Record<string, Meta> = {
  '/': {
    title: 'MENDWELL | In-Home Appliance Repair Services',
    description: 'MENDWELL provides careful in-home repair for refrigerators, washers, dryers, dishwashers, ovens and air conditioners. Request a repair visit online in minutes.',
  },
  '/services': {
    title: `Appliance Repair Services | ${BRAND}`,
    description: 'Explore MENDWELL in-home repair for refrigerators, washers and dryers, dishwashers, ovens and ranges, and air conditioning units.',
  },
  '/pricing': {
    title: `Repair Pricing & Estimates | ${BRAND}`,
    description: 'See how MENDWELL pricing works: published repair ranges, service call details and a clear estimate before any repair begins.',
  },
  '/technicians': {
    title: `Our Technicians | ${BRAND}`,
    description: 'Meet the MENDWELL technicians behind our in-home appliance repairs, organized by refrigeration, laundry and kitchen specialties.',
  },
  '/about': {
    title: `About Us | ${BRAND}`,
    description: 'Learn about MENDWELL, an in-home appliance repair service built on clear estimates, careful work and respect for your home.',
  },
  '/service-areas': {
    title: `Service Areas | ${BRAND}`,
    description: 'Check where MENDWELL provides in-home appliance repair. Include your address in a repair request and we will confirm coverage.',
  },
  '/reviews': {
    title: `Customer Reviews | ${BRAND}`,
    description: 'Read what customers say about MENDWELL appliance repair visits, from refrigerators and washers to dishwashers and ovens.',
  },
  '/book': {
    title: `Book an Appliance Repair | ${BRAND}`,
    description: 'Request an in-home appliance repair visit with MENDWELL. Choose your appliance, describe the issue and pick a preferred time window.',
  },
};

const NOT_FOUND: Meta = {
  title: `Page Not Found | ${BRAND}`,
  description: 'The page you are looking for could not be found. Return to MENDWELL to explore appliance repair services.',
};

function resolveMeta(pathname: string): Meta {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (STATIC_META[path]) return STATIC_META[path];

  const svcMatch = path.match(/^\/services\/([^/]+)$/);
  if (svcMatch) {
    const svc = services.find((s) => s.slug === svcMatch[1]);
    if (svc) return { title: `${svc.name} | ${BRAND}`, description: `${svc.summary} ${BRAND} offers careful in-home ${svc.name.toLowerCase()}.` };
  }

  const techMatch = path.match(/^\/technicians\/([^/]+)$/);
  if (techMatch) {
    const tech = technicians.find((t) => t.slug === techMatch[1]);
    if (tech) return { title: `${tech.name}, ${tech.role} | ${BRAND}`, description: tech.summary };
  }

  return NOT_FOUND;
}

function setMeta(selector: string, attr: 'name' | 'property', key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

// Sets per-route title and description only. Renders nothing.
export default function RouteMeta() {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = resolveMeta(pathname);
    document.title = meta.title;
    setMeta('meta[name="description"]', 'name', 'description', meta.description);
    setMeta('meta[property="og:title"]', 'property', 'og:title', meta.title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', meta.description);
  }, [pathname]);

  return null;
}